import {
	createFileRoute,
	Link,
	Outlet,
	useMatches,
} from "@tanstack/react-router";
import { PublicPageLayout } from "@/components/PublicPageLayout";
import { PublicFooter } from "@/components/public-layout";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

export const Route = createFileRoute("/docs")({
	component: DocsLayout,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({
			matches,
			internalPath: "/docs",
		});

		return {
			meta: [
				{ title: "AgentMRR Docs - List, Verify & Track AI Agent MRR" },
				{
					name: "description",
					content:
						"Docs for AgentMRR: list your AI agent, verify revenue with Stripe, and connect any Agent-Auth-capable agent over plain HTTP.",
				},
				{
					property: "og:title",
					content: "AgentMRR Docs - List, Verify & Track AI Agent MRR",
				},
				{
					property: "og:description",
					content:
						"Docs for AgentMRR: list your AI agent, verify revenue with Stripe, and connect any Agent-Auth-capable agent over plain HTTP.",
				},
				{
					property: "og:image",
					content: ogImageUrl("docs"),
				},
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("docs") },
			],
			links: buildLocalizedSeoLinks({
				matches,
				internalPath: "/docs",
			}),
		};
	},
});

export const docsSections = [
	{
		group: "Getting started",
		items: [{ slug: "submit", label: "Submit an agent" }],
	},
] as const;

function Sidebar({ currentSlug }: { currentSlug: string | null }) {
	return (
		<nav className="space-y-6">
			{docsSections.map((section) => (
				<div key={section.group}>
					<p className="mb-3 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
						&gt; {section.group}
					</p>
					<ul className="space-y-0.5 border-l border-border">
						{section.items.map((item) => {
							const isActive = currentSlug === item.slug;
							return (
								<li key={item.slug}>
									<Link
										to={`/docs/${item.slug}`}
										className={`block border-l-2 px-3 py-1.5 font-mono text-sm uppercase tracking-[0.12em] transition-colors -ml-px ${
											isActive
												? "border-brand text-brand"
												: "border-transparent text-muted-foreground hover:border-brand/40 hover:text-brand"
										}`}
									>
										{item.label}
									</Link>
								</li>
							);
						})}
					</ul>
				</div>
			))}
		</nav>
	);
}

function DocsLayout() {
	const matches = useMatches();
	const lastMatch = matches[matches.length - 1];
	const currentSlug = lastMatch?.fullPath.replace("/docs/", "") || null;

	return (
		<PublicPageLayout>
			<div className="mx-auto w-full max-w-5xl pt-4 pb-24 sm:pt-6">
				<div className="flex gap-12">
					{/*
						Sidebar visibility keys off the content COLUMN width (@.../page):
						at xl the ad sidebars shrink the column to ~497px, so showing the
						208px sidebar there would crush `main`. It only appears once the
						column can afford it. `main` is its own @container/docs so docs
						content responds to main's real width (column minus sidebar).
					*/}
					<aside className="hidden @3xl/page:block w-52 shrink-0">
						<div className="sticky top-28">
							<Sidebar currentSlug={currentSlug} />
						</div>
					</aside>

					<main className="@container/docs min-w-0 flex-1">
						<Outlet />
					</main>
				</div>
			</div>
			<PublicFooter />
		</PublicPageLayout>
	);
}
